import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  /**
   * Server-side environment variables.
   * Previously inherited from @poseidon/server/env via `extends`, now inlined
   * so Next.js does not need to transpile the server package.
   */
  server: {
    BRANCH: z.string().optional(),
  },

  /**
   * Shared environment variables (available on both server and client).
   */
  shared: {
    // NODE_ENV is build-time-inlined by Next on both server and client. It lives in
    // `shared` (the canonical T3 placement) so client components can read env.NODE_ENV
    // without tripping "Attempted to access a server-side env var on the client".
    NODE_ENV: z.enum(['development', 'test', 'production']),
    NEXT_PUBLIC_BACKEND_URL: z.string().min(1),
    NEXT_PUBLIC_NGROK_URL: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: z.string().min(1),
    NEXT_PUBLIC_FIREBASE_API_KEY: z.string().min(1),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z
      .string()
      .optional()
      .transform((s) => (s?.startsWith('BAKED_') ? undefined : s)),
    NEXT_PUBLIC_APP_VERSION: z.string().default('main'),
  },

  /**
   * Client-side environment variables.
   */
  client: {
    NEXT_PUBLIC_FIREBASE_APP_ID: z.string().min(1),
    NEXT_PUBLIC_FINGERPRINT_API_KEY: z.string().min(1),
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: z.string().min(1),
    NEXT_PUBLIC_EXTENSION_URL: z.string().min(1),
    NEXT_PUBLIC_IMAGE_RESIZING_SERVER_URL: z.string().min(1),
    NEXT_PUBLIC_DISABLE_REFETCH_ON_WINDOW_FOCUS: z
      .string()
      .optional()
      .transform((s) => {
        if (s == null) return false;
        return s === 'true';
      }),
    NEXT_PUBLIC_GMAIL_CLIENT_ID: z.string().min(1),
    NEXT_PUBLIC_GTM: z
      .string()
      .optional()
      .transform((s) => (s?.startsWith('BAKED_') ? undefined : s)),
  },

  experimental__runtimeEnv: {
    ...process.env,
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_BACKEND_URL: process.env.NEXT_PUBLIC_BACKEND_URL,
    NEXT_PUBLIC_NGROK_URL: process.env.NEXT_PUBLIC_NGROK_URL,
    NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT: process.env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    NEXT_PUBLIC_FINGERPRINT_API_KEY: process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY,
    NEXT_PUBLIC_RECAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
    NEXT_PUBLIC_EXTENSION_URL: process.env.NEXT_PUBLIC_EXTENSION_URL,
    NEXT_PUBLIC_IMAGE_RESIZING_SERVER_URL: process.env.NEXT_PUBLIC_IMAGE_RESIZING_SERVER_URL,
    NEXT_PUBLIC_DISABLE_REFETCH_ON_WINDOW_FOCUS: process.env.NEXT_PUBLIC_DISABLE_REFETCH_ON_WINDOW_FOCUS,
    NEXT_PUBLIC_GMAIL_CLIENT_ID: process.env.NEXT_PUBLIC_GMAIL_CLIENT_ID,
    NEXT_PUBLIC_GTM: process.env.NEXT_PUBLIC_GTM,
  },

  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  emptyStringAsUndefined: true,
});
